import React, { useRef, useState } from 'react';
import { Play, Star, X } from 'lucide-react';
import { normalizeEmbedUrl, getVideoThumbnail } from '../../lib/embed';
import { SectionHead, CtaButton, Lightbox, Reveal, ProtectedImage, SCROLL_MT } from './shared';

const AVATAR_STYLES = [
  'from-blue-500 to-indigo-600', 'from-amber-400 to-orange-500', 'from-emerald-400 to-teal-600',
  'from-rose-400 to-pink-600', 'from-violet-500 to-purple-600', 'from-cyan-400 to-blue-600',
];

function Stars({ size = 12 }) {
  return <span className="flex items-center gap-0.5 text-amber-500">{[0, 1, 2, 3, 4].map(j => <Star key={j} size={size} fill="currentColor" stroke="none" />)}</span>;
}

function VideoCard({ t, i, onPlay }) {
  const thumb = t.image_url || getVideoThumbnail(t.video_url);
  return (
    <Reveal delay={i * 0.05}>
      <button onClick={() => onPlay(t)} data-testid={`video-testimonial-${i}`}
        className="group relative block w-full text-left rounded-2xl overflow-hidden bg-slate-900 text-white shadow-md hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 aspect-[4/5] sm:aspect-[4/3]">
        {thumb && <img src={thumb} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-80 group-hover:scale-[1.03] transition-all duration-500" />}
        <span className="absolute inset-0 bg-gradient-to-b from-slate-950/85 via-slate-950/30 to-slate-950/80" aria-hidden="true" />
        <div className="absolute inset-0 p-3.5 sm:p-4 flex flex-col">
          {t.quote && <p className="text-[13px] sm:text-[14px] font-semibold italic leading-snug line-clamp-4 drop-shadow" data-testid={`video-testimonial-quote-${i}`}>“{t.quote}”</p>}
          <div className="mt-auto flex items-end justify-between gap-2">
            <div className="min-w-0">
              {t.result && <p className="text-[11px] text-amber-300 font-semibold truncate">{t.result}</p>}
              <p className="text-[11.5px] font-extrabold tracking-[0.12em] uppercase truncate">{t.name}</p>
            </div>
            <span className="shrink-0 w-9 h-9 rounded-full border-2 border-amber-400 bg-slate-950/60 backdrop-blur flex items-center justify-center text-amber-400 group-hover:bg-amber-400 group-hover:text-slate-900 transition-colors">
              <Play size={13} fill="currentColor" />
            </span>
          </div>
        </div>
      </button>
    </Reveal>
  );
}

function VideoModal({ t, onClose }) {
  if (!t) return null;
  return (
    <div className="fixed inset-0 z-[90] bg-slate-950/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="video-modal">
      <div className="relative w-full max-w-4xl" onClick={e => e.stopPropagation()}>
        <div className="relative rounded-2xl overflow-hidden bg-black shadow-2xl" style={{ aspectRatio: '16 / 9' }}>
          <iframe src={normalizeEmbedUrl(t.video_url, { autoplay: true })} title={t.name || 'Student story'} allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" className="absolute inset-0 w-full h-full" data-testid="video-modal-iframe" />
        </div>
        {(t.name || t.result) && <p className="mt-3 text-center text-white/80 text-sm"><span className="font-bold text-white">{t.name}</span>{t.result && <span className="text-amber-300"> · {t.result}</span>}</p>}
        <button onClick={onClose} className="absolute -top-3 -right-3 w-10 h-10 rounded-full bg-white text-slate-900 flex items-center justify-center shadow-lg" data-testid="video-modal-close"><X size={18} /></button>
      </div>
    </div>
  );
}

function QuoteCard({ t, i }) {
  return (
    <div className="h-full p-5 rounded-2xl bg-white border border-neutral-200 hover:shadow-xl transition-shadow duration-300" data-testid={`text-testimonial-${i}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className={`w-9 h-9 shrink-0 rounded-full bg-gradient-to-br ${AVATAR_STYLES[i % 6]} text-white font-bold text-sm flex items-center justify-center`}>{(t.name || 'S')[0]}</span>
          <div className="min-w-0">
            <p className="text-[13.5px] font-bold text-slate-900 truncate">{t.name}</p>
            {t.result && <p className="text-[11.5px] text-amber-700 font-bold truncate">{t.result}</p>}
          </div>
        </div>
        <Stars size={11} />
      </div>
      <p className="text-[14px] text-slate-600 leading-relaxed">“{t.quote}”</p>
    </div>
  );
}

function QuoteSlider({ quotes }) {
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const onScroll = () => {
    const el = ref.current;
    if (!el || !el.firstElementChild) return;
    const w = el.firstElementChild.getBoundingClientRect().width + 12;
    setActive(Math.min(quotes.length - 1, Math.round(el.scrollLeft / w)));
  };
  return (
    <>
      <div ref={ref} onScroll={onScroll} className="sm:hidden flex gap-3 overflow-x-auto snap-x snap-mandatory -mx-4 px-4 pb-1 cmx-no-scrollbar" data-testid="results-quotes-slider">
        {quotes.map((t, i) => <div key={t.id} className="snap-center shrink-0 w-[86%]"><QuoteCard t={t} i={i} /></div>)}
      </div>
      {quotes.length > 1 && (
        <div className="sm:hidden mt-3 flex justify-center gap-1.5" data-testid="results-quotes-dots">
          {quotes.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === active ? 'w-5 bg-slate-900' : 'w-1.5 bg-slate-300'}`} />)}
        </div>
      )}
      <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="results-quotes">
        {quotes.map((t, i) => <QuoteCard key={t.id} t={t} i={i} />)}
      </div>
    </>
  );
}

export default function Results({ results, videos, images, quotes, onCta, protection }) {
  const [open, setOpen] = useState(null);
  const [playing, setPlaying] = useState(null);
  if (!videos.length && !images.length && !quotes.length) return null;
  return (
    <section id="results" className={`${SCROLL_MT} py-12 sm:py-16 lg:py-20 bg-white/40 border-y border-white`} data-testid="results-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <SectionHead label={results.label} title={results.title} subtitle={results.subtitle} className="mb-8 sm:mb-10" />

        {videos.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4" data-testid="results-videos">
            {videos.map((t, i) => <VideoCard key={t.id} t={t} i={i} onPlay={setPlaying} />)}
          </div>
        )}

        {images.length > 0 && (
          <div className={`${videos.length ? 'mt-3 sm:mt-4' : ''} columns-2 lg:columns-3 gap-3 sm:gap-4 [&>button]:mb-3 sm:[&>button]:mb-4`} data-testid="results-screenshots">
            {images.map((t, i) => (
              <button key={t.id} onClick={() => setOpen(t)} data-testid={`image-testimonial-${i}`}
                className="block w-full break-inside-avoid rounded-2xl overflow-hidden bg-white border border-neutral-200 shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300">
                <ProtectedImage src={t.image_url} alt={t.name || 'Student result'} protection={protection} imgClassName="w-full h-auto block" />
              </button>
            ))}
          </div>
        )}

        {quotes.length > 0 && (
          <div className={videos.length || images.length ? 'mt-8 sm:mt-10' : ''}>
            <QuoteSlider quotes={quotes} />
          </div>
        )}

        {results.cta_label && (
          <div className="mt-9 sm:mt-10 text-center">
            <CtaButton label={results.cta_label} onClick={() => onCta('results')} variant="dark" size="md" className="w-full sm:w-auto" testId="results-cta" />
          </div>
        )}
      </div>
      <Lightbox src={open?.image_url} alt={open?.name} onClose={() => setOpen(null)} protection={protection} />
      <VideoModal t={playing} onClose={() => setPlaying(null)} />
    </section>
  );
}
